import React from "react";
import { priorityBand } from "../../pages/Events/trendUtils";

export type PriorityBand = "critical" | "high" | "elevated" | "low" | "none";

type Props = {
  /** 0-100 priority score. Drives sorting elsewhere; shown here only as a band
   *  word. Null/undefined renders the unknown dash. */
  score?: number | null;
  /** Band from trendUtils. If omitted, derived from the score. */
  band?: string | null;
  className?: string;
};

const bandStyle: Record<PriorityBand, { chip: string; label: string }> = {
  critical: { chip: "bg-alert-critical/15 text-alert-critical", label: "Critical" },
  high: { chip: "bg-alert-fire/15 text-alert-fire", label: "High" },
  elevated: { chip: "bg-alert-warning/20 text-alert-warning", label: "Elevated" },
  low: { chip: "bg-black/5 text-ink-2", label: "Low" },
  none: { chip: "", label: "None" },
};

/**
 * "Most problematic" priority shown as a band word (Critical down to muted None)
 * on a tinted chip. The underlying 0-100 score still drives table sorting and
 * feed ordering but isn't surfaced, since it has no standalone meaning to users.
 *
 * A healthy sensor and one with nothing loaded yet both render a muted dash.
 * A sensor too new at its location to judge does too, deliberately: the Trend
 * column beside this one already marks it unknown, so saying it twice earned
 * nothing. Severity is unaffected either way -- the score reads the recent
 * window alone, so a new sensor in a dangerous kitchen still reaches a band.
 *
 * The band thresholds are NOT duplicated here. They live in
 * `trendUtils.PRIORITY_THRESHOLDS` and are read through `priorityBand`, because
 * a second copy is the defect this repo keeps finding: a rule asserted in two
 * places where only one gets updated.
 */
export default function PriorityBadge({ score, band, className = "" }: Props) {
  const key =
    score == null
      ? null
      : (band as PriorityBand) in bandStyle
        ? (band as PriorityBand)
        : (priorityBand(score).band as PriorityBand);

  if (key == null || key === "none") {
    return (
      <span className="text-ink-3" title="Priority: None" aria-label="No priority">
        -
      </span>
    );
  }

  const { chip, label } = bandStyle[key];
  return (
    <span
      className={`inline-flex items-center rounded-chip px-2.5 py-1 text-xs font-bold ${chip} ${className}`}
      title={`Priority: ${label}`}
    >
      {label}
    </span>
  );
}
